import { Chip, Stack, Typography } from "@mui/material";
import { useState } from "react";
import StatusChip from "@entrilia/shared/components/StatusChip";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import type { AccessRole, InvitationStatus, OrganizationMember } from "../../../../../api/types/userManagementTypes";
import MembersContext from "../MembersContext";
import OrganizationRoleStatusTag from "../OrganizationRoleStatusTag";
import { filterOrganizationRoles } from "../definitions";
import CompaniesListPopover from "./CompaniesListPopover";

interface OrganizationMemberProps {
  member: OrganizationMember;
}

export const MemberRoleCell = ({ roles }: { roles: AccessRole[] }) => {
  const organizationRoles = filterOrganizationRoles(roles);
  return (
    <Stack direction="row" spacing={0.5} sx={{ alignContent: "center" }}>
      {organizationRoles.map((role) => (
        <OrganizationRoleStatusTag key={role} role={role} />
      ))}
    </Stack>
  );
};

export const CompanyCell = ({ member }: OrganizationMemberProps) => {
  const [popUpAnchorEl, setPopUpAnchorEl] = useState<HTMLElement | undefined>();
  const { companiesList } = useDefinedContext(MembersContext);

  if (member.hasAccessToAllClients) {
    return <Typography>All</Typography>;
  }

  if (member.clientsAccess.length === 0) {
    return <Typography>None</Typography>;
  }

  const filteredCompaniesList = companiesList.filter((company) =>
    member.clientsAccess.some((client) => client.clientCode === company.value)
  );

  const [firstClient] = filteredCompaniesList;
  const restCompanies = filteredCompaniesList.slice(1);

  const toggleShowCompanies = (event: React.MouseEvent<HTMLElement>) => {
    setPopUpAnchorEl(popUpAnchorEl ? undefined : event.currentTarget);
  };

  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
      {firstClient && <Typography>{firstClient?.label}</Typography>}
      {restCompanies.length > 0 && <Chip onClick={toggleShowCompanies} label={"+" + restCompanies.length} />}
      <CompaniesListPopover
        anchorEl={popUpAnchorEl}
        onClose={() => setPopUpAnchorEl(undefined)}
        companiesWithAccess={restCompanies}
      />
    </Stack>
  );
};

export const InvitationStatusCell = ({ status }: { status: InvitationStatus }) => {
  switch (status) {
    case "PendingApproval": {
      return (
        <StatusChip
          label="Pending Approval"
          withDot
          color={(t) => t.palette.warning.main}
          tooltipText="This user is awaiting approval by Entrilia to verify the details. The invitation email will be sent once the review is complete."
        />
      );
    }
    case "Sent": {
      return <StatusChip label="Approved" withDot color={(t) => t.palette.success.main} />;
    }
    case "Rejected": {
      return (
        <StatusChip
          label="Rejected"
          withDot
          color={(t) => t.palette.error.main}
          tooltipText="This invitation was rejected during approval. The invitation email was not sent."
        />
      );
    }
    default: {
      return null;
    }
  }
};
